import { planTypes } from "./constants";

/**
 * Plan Logo Map
 *
 * Centralized mapping of plan types to their logo assets.
 * This map consolidates logo definitions from all plan mixins for easy access.
 *
 * Usage:
 * import planLogoMap from '@/common/plan-logo-map';
 * const logo = planLogoMap[planType];
 */

const planLogoMap = {
  // Meeting & Core
  [planTypes.ZOOM_MEETING]: require('@/assets/plans/icon_ZM.svg'),
  [planTypes.ZOOM_ONE_BUNDLE]: require('@/assets/plans/icon_ZM.svg'),

  // Events
  [planTypes.EVENT_SAAS]: require('@/assets/plans/eventsaas.svg'),
  [planTypes.EVENT_SAAS_CORE]: require('@/assets/plans/eventsaascore.svg'),
  [planTypes.EVENT_SERVICES]: require('@/assets/plans/eventsaas.svg'), // reuse

  // Webinar & Meeting Add-ons
  [planTypes.WEBINAR]: require('@/assets/plans/webinar.svg'),
  [planTypes.LARGE_MEETING]: require('@/assets/plans/large_meeting.svg'),
  [planTypes.CONCURRENCY_MEETING_BASIC]: require('@/assets/plans/concurrent-meeting-basic.svg'),
  [planTypes.CONCURRENT_MEETING_PLUS]: require('@/assets/plans/concurrent-meeting-basic.svg'),

  // Hosts & Editors
  [planTypes.ADDITIONAL_HOST]: require('@/assets/plans/additional-host.svg'),
  [planTypes.ADDITIONAL_CO_EDITOR]: require('@/assets/plans/additional-co-editor.svg'),

  // Recording & Storage
  [planTypes.CMR]: require('@/assets/plans/cloud.svg'),
  [planTypes.LDKCMR]: require('@/assets/plans/cloud.svg'),

  // Collaboration Tools
  [planTypes.ZOOM_WHITEBOARD]: require('@/assets/plans/logo_whiteboard.svg'),
  [planTypes.ZOOM_DOCS]: require('@/assets/plans/logo_docs.svg'),
  [planTypes.ZOOM_CLIPS]: require('@/assets/plans/zoom-clips-plus.svg'),
  [planTypes.ZOOM_SCHEDULER]: require('@/assets/plans/zoom_scheduler.svg'),

  // AI & Captions
  [planTypes.ZM_AI_COMPANION]: require('@/assets/plans/logo_zoom_custom_ai_companion.svg'),
  [planTypes.ZOOM_CUSTOM_AI_COMPANION]: require('@/assets/plans/logo_zoom_custom_ai_companion.svg'),
  [planTypes.TRANSLATION_CAPTIONS]: require('@/assets/plans/translated_captions.svg'),
  [planTypes.CUSTOM_AVATARS]: require('@/assets/plans/logo_custom_avatars.svg'),

  // Rooms & Devices
  [planTypes.ZOOM_ROOMS]: require('@/assets/plans/zoom_room.svg'),
  [planTypes.ROOM_CONNECTOR]: require('@/assets/plans/conference_room_connector.svg'),
  [planTypes.ZOOM_DEVICE]: require('@/assets/plans/hardware_desk_phones.svg'),
  [planTypes.ZOOM_DEVICE$DESK_PHONE]: require('@/assets/plans/hardware_desk_phones.svg'),
  [planTypes.ZOOM_DEVICE$HEADSET]: require('@/assets/plans/hardware_headsets.svg'),

  // Workspace
  [planTypes.WORKSPACE_RESERVATION]: require('@/assets/plans/logo_workspace_reservation.svg'),
  [planTypes.VISITOR_MANAGEMENT]: require('@/assets/plans/visitor_management.svg'),

  // Content Management
  [planTypes.ZCM]: require('@/assets/plans/zcm.svg'),
  [planTypes.ZCM_STORAGE]: require('@/assets/plans/zcm.svg'),
  [planTypes.VIDEO_MANAGEMENT]: require('@/assets/plans/zoom_video_management.svg'),

  // SDK
  [planTypes.VIDEO_SDK]: require('@/assets/plans/video_sdk.svg'),
  [planTypes.VIDEO_SDK_UC]: require('@/assets/plans/sdkuc.svg'),

  // Phone & Audio
  [planTypes.TOLLFREE]: require('@/assets/plans/audio_conferencing.svg'),
  [planTypes.TOLLFREE$DID]: require('@/assets/plans/dedicated_dial_in_number.svg'),
  [planTypes.ZOOM_PHONE]: require('@/assets/plans/logo_pbx.svg'),
  [planTypes.ZOOM_PHONE$ZPA]: require('@/assets/plans/zoom_phone_power_pack.svg'),
  [planTypes.ZOOM_PHONE$ZPI]: require('@/assets/plans/zoom_phone_international_calling.svg'),
  [planTypes.ZOOM_PHONE$ADDITIONAL]: require('@/assets/plans/zoom_phone_additional.svg'),
  [planTypes.ZOOM_PHONE$AICONCIERGE]: require('@/assets/plans/zoom_phone_ai_concierge.svg'),

  // Education & Bundles
  [planTypes.ZONE_EDU_PLUS]: require('@/assets/plans/icon_ZM.svg'),
  [planTypes.ZONE_PRO_PLUS]: require('@/assets/plans/icon_ZM.svg'),
  [planTypes.ZONE_BASIC_PLUS]: require('@/assets/plans/icon_ZM.svg'),

  // Partners
  [planTypes.MASTER_CLASS]: require('@/assets/partner/master-class-logo.svg'),

  // Revenue
  [planTypes.ZOOM_REVENUE_ACCELERATOR]: require('@/assets/plans/zoom-revenue-accelerator.svg'),

  [planTypes.ZOOM_ENHANCED_MEDIA]: require('@/assets/plans/logo_zoom_enhanced_media.svg'),

  // TODO: replace with actual Zoom Email & Calendar logo asset when available
  [planTypes.ZOOM_EMAIL_CALENDAR]: require('@/assets/plans/logo_zoom_email_calendar.svg'),
};
/**
 * Get logo for a plan type with fallback
 * @param {string} planType - Plan type constant (e.g., 'zoom_meeting', 'large_meeting')
 * @param {string} fallback - Fallback logo URL (optional)
 * @returns {string|null} Logo asset or fallback
 */
export function getPlanLogo(planType, fallback = null) {
  if (!planType) {
    console.warn('[Plan Logo Map] planType is required');
    return fallback;
  }

  // planTypes values are already lowercase (e.g., 'zoom_meeting', 'large_meeting')
  // so we convert to lowercase for consistency
  const logo = planLogoMap[planType.toLowerCase()];

  if (!logo && fallback === null) {
    console.warn(`[Plan Logo Map] No logo found for plan type: ${planType}`);
  }

  return logo || fallback;
}

// Default export
export default planLogoMap;


